import type { ChipProps } from "@mui/material";
import type { ParseKeys } from "i18next";
import {
  EventStatus,
  ParticipantRole,
  ParticipantStatus,
} from "../types/events-unified";
import { DEFAULT_CURRENCY } from "../utils/currency";

/** Цвет и русскоязычная метка для каждого статуса мероприятия. */
export const EVENT_STATUS_CONFIG: Record<
  EventStatus,
  // `NonNullable`: цвет у каждого статуса задан, а `ChipProps['color']` включает
  // `undefined` (проп необязательный) — при exactOptionalPropertyTypes такой
  // цвет нельзя передать в `<Chip color>`.
  { color: NonNullable<ChipProps["color"]>; label: string }
> = {
  [EventStatus.DRAFT]: { color: "default", label: "Черновик" },
  [EventStatus.SCHEDULED]: { color: "info", label: "Запланировано" },
  [EventStatus.PUBLISHED]: { color: "success", label: "Опубликовано" },
  [EventStatus.ONGOING]: { color: "warning", label: "Идёт сейчас" },
  [EventStatus.COMPLETED]: { color: "info", label: "Завершено" },
  [EventStatus.CANCELLED]: { color: "error", label: "Отменено" },
  [EventStatus.POSTPONED]: { color: "default", label: "Отложено" },
};

/**
 * Подпись статуса мероприятия для человека — ключ словаря `events`. Метка
 * `EVENT_STATUS_CONFIG.label` выше русская при любом языке интерфейса, поэтому
 * экран, который показывает статус, берёт подпись отсюда через `t`. Ключ
 * проверяет компилятор: эталонный словарь типизирован (`i18next.d.ts`).
 */
export const EVENT_STATUS_LABEL_KEYS: Record<
  EventStatus,
  ParseKeys<"events">
> = {
  [EventStatus.DRAFT]: "list.statusValues.draft",
  [EventStatus.SCHEDULED]: "list.statusValues.scheduled",
  [EventStatus.PUBLISHED]: "list.statusValues.published",
  [EventStatus.ONGOING]: "list.statusValues.ongoing",
  [EventStatus.COMPLETED]: "list.statusValues.completed",
  [EventStatus.CANCELLED]: "list.statusValues.cancelled",
  [EventStatus.POSTPONED]: "list.statusValues.postponed",
};

/** Цвет и русскоязычная метка для каждого статуса участника. */
export const PARTICIPANT_STATUS_CONFIG: Record<
  ParticipantStatus,
  // `NonNullable`: цвет у каждого статуса задан, а `ChipProps['color']` включает
  // `undefined` (проп необязательный) — при exactOptionalPropertyTypes такой
  // цвет нельзя передать в `<Chip color>`.
  { color: NonNullable<ChipProps["color"]>; label: string }
> = {
  [ParticipantStatus.REGISTERED]: {
    color: "default",
    label: "Зарегистрирован",
  },
  [ParticipantStatus.CONFIRMED]: { color: "success", label: "Подтверждён" },
  [ParticipantStatus.CANCELED]: { color: "error", label: "Отменён" },
  [ParticipantStatus.ATTENDED]: { color: "success", label: "Присутствовал" },
  [ParticipantStatus.WAITLISTED]: { color: "warning", label: "В очереди" },
  [ParticipantStatus.INVITED]: { color: "info", label: "Приглашён" },
  [ParticipantStatus.PENDING]: { color: "warning", label: "Ожидает" },
  [ParticipantStatus.REJECTED]: { color: "error", label: "Отклонён" },
  [ParticipantStatus.NO_SHOW]: { color: "default", label: "Не явился" },
};

/**
 * Подпись статуса участника для человека — ключ словаря `events`. Метка
 * `PARTICIPANT_STATUS_CONFIG.label` выше русская при любом языке интерфейса,
 * поэтому экран, который показывает статус, берёт подпись отсюда через `t`.
 * Карту читают регистрация на странице события и таблица участников экрана
 * администрирования; страница участников собирает те же ключи шаблонной
 * строкой. Ключ проверяет компилятор: эталонный словарь типизирован
 * (`i18next.d.ts`).
 */
export const PARTICIPANT_STATUS_LABEL_KEYS: Record<
  ParticipantStatus,
  ParseKeys<"events">
> = {
  [ParticipantStatus.REGISTERED]: "participants.status.values.registered",
  [ParticipantStatus.CONFIRMED]: "participants.status.values.confirmed",
  [ParticipantStatus.CANCELED]: "participants.status.values.cancelled",
  [ParticipantStatus.ATTENDED]: "participants.status.values.attended",
  [ParticipantStatus.WAITLISTED]: "participants.status.values.waitlisted",
  [ParticipantStatus.INVITED]: "participants.status.values.invited",
  [ParticipantStatus.PENDING]: "participants.status.values.pending",
  [ParticipantStatus.REJECTED]: "participants.status.values.rejected",
  [ParticipantStatus.NO_SHOW]: "participants.status.values.no_show",
};

/**
 * Подпись РОЛИ участника для человека — ключ словаря `events`. Карта жила
 * внутри `EventParticipantsList.tsx`, и когда вкладку «История» кабинета
 * участника понадобилось научить показывать мою роль (AD-415/AD-418), подпись
 * пришлось бы писать заново — то есть завести второй словарь ролей рядом с
 * первым. Карта переехала сюда, к статусам, чтобы читатель был один. Ключ
 * проверяет компилятор: эталонный словарь типизирован (`i18next.d.ts`).
 */
export const PARTICIPANT_ROLE_LABEL_KEYS: Record<
  ParticipantRole,
  ParseKeys<"events">
> = {
  [ParticipantRole.ATTENDEE]: "participants.role.values.attendee",
  [ParticipantRole.SPEAKER]: "participants.role.values.speaker",
  [ParticipantRole.MODERATOR]: "participants.role.values.moderator",
  [ParticipantRole.ORGANIZER]: "participants.role.values.organizer",
  [ParticipantRole.CO_ORGANIZER]: "participants.role.values.co_organizer",
  [ParticipantRole.VOLUNTEER]: "participants.role.values.volunteer",
  [ParticipantRole.SPONSOR]: "participants.role.values.sponsor",
  [ParticipantRole.MEDIA]: "participants.role.values.media",
};

/**
 * Опции для выпадающего списка фильтрации по статусу мероприятия — ТОЛЬКО
 * значения. AD-358: пока сюда попадала и `label` из `EVENT_STATUS_CONFIG`,
 * читатель мог взять готовую подпись, а она русская при любом языке
 * интерфейса; так и случилось с фильтром `EventSearch.tsx`. Подпись берётся
 * через `t` по `EVENT_STATUS_LABEL_KEYS` — тем же способом, что и везде.
 */
export const EVENT_STATUS_OPTIONS = Object.entries(EVENT_STATUS_CONFIG).map(
  ([value]) => ({ value: value as EventStatus }),
);

/** Опции для выпадающего списка ФИЛЬТРАЦИИ по статусу участника (все статусы —
 *  фильтр по терминальному NO_SHOW/REJECTED легитимен). Для управляющей смены
 *  статуса участника используйте getSettableParticipantStatusOptions — не этот
 *  плоский список (иначе UI предложит переход, который BE отклонит 400). */
export const PARTICIPANT_STATUS_OPTIONS = Object.entries(
  PARTICIPANT_STATUS_CONFIG,
).map(([value, { label }]) => ({ value: value as ParticipantStatus, label }));

/**
 * State-машина статусов участника — ЗЕРКАЛО BE-канона
 * (Backend `modules/events/constants/participant-status.constants.ts`,
 * PARTICIPANT_STATUS_TRANSITIONS, EVT-004). SSOT — на BE (FE не может
 * импортировать BE-пакет); при правке карты на BE синхронизировать здесь.
 * BE применяет карту в EventAdminService.updateParticipantStatus: цель вне
 * списка текущего статуса → 400. Поэтому управляющий dropdown обязан предлагать
 * ТОЛЬКО валидные переходы, а не весь enum (иначе — мёртвая опция).
 * Терминальные (ATTENDED/CANCELED/REJECTED/NO_SHOW) исходящих переходов не имеют.
 */
export const PARTICIPANT_STATUS_TRANSITIONS: Record<
  ParticipantStatus,
  ParticipantStatus[]
> = {
  [ParticipantStatus.PENDING]: [
    ParticipantStatus.REGISTERED,
    ParticipantStatus.REJECTED,
  ],
  [ParticipantStatus.WAITLISTED]: [ParticipantStatus.REGISTERED],
  [ParticipantStatus.REGISTERED]: [
    ParticipantStatus.CONFIRMED,
    ParticipantStatus.ATTENDED,
  ],
  [ParticipantStatus.CONFIRMED]: [ParticipantStatus.ATTENDED],
  [ParticipantStatus.INVITED]: [
    ParticipantStatus.REGISTERED,
    ParticipantStatus.CANCELED,
  ],
  [ParticipantStatus.ATTENDED]: [],
  [ParticipantStatus.CANCELED]: [],
  [ParticipantStatus.REJECTED]: [],
  [ParticipantStatus.NO_SHOW]: [],
};

/** Допустимые целевые статусы (value+label) для управляющей смены статуса
 *  участника из текущего `current`. Пустой массив = терминальный статус
 *  (смена невозможна — dropdown блокируется).
 *  `current` — строка, как и у canTransitionParticipantStatus: статус приходит из
 *  ответа сервера литеральным типом генерата, а не членом FE-enum (обратное
 *  присваивание литерала в enum TypeScript запрещает). Неизвестный ключ → лукап
 *  undefined → пустой список → dropdown заблокирован, а не «все переходы». */
export const getSettableParticipantStatusOptions = (
  current: string,
): { value: ParticipantStatus; label: string }[] =>
  (PARTICIPANT_STATUS_TRANSITIONS[current as ParticipantStatus] ?? []).map(
    (value) => ({
      value,
      label: PARTICIPANT_STATUS_CONFIG[value].label,
    }),
  );

/** true, если переход current→target разрешён BE-картой. `current` — строка
 *  (статус из ответа BE): неизвестный ключ → лукап undefined → false. Для
 *  показа быстрых кнопок смены статуса только там, где переход валиден. */
export const canTransitionParticipantStatus = (
  current: string,
  target: ParticipantStatus,
): boolean =>
  (PARTICIPANT_STATUS_TRANSITIONS[current as ParticipantStatus] ?? []).includes(
    target,
  );

/** Действия очереди модерации, которые сервер исполняет над регистрацией.
 *  Остальные четыре (`hide`/`delete`/`warn`/`ban`) он отвергает с 400 — на
 *  экране их нет с `AD-188`. */
export type ModerationRowAction = "approve" | "reject";

/**
 * AD-288. Исполнимость действия очереди модерации на конкретной строке.
 *
 * Очередь модерации кабинета события — это очередь РЕГИСТРАЦИЙ: сервер отдаёт
 * участников события, а `approve`/`reject` переводят участника по той же карте
 * `PARTICIPANT_STATUS_TRANSITIONS` выше. Значит экран обязан скрывать кнопку,
 * которую на этой строке исполнить нельзя, — по тому же правилу, по которому
 * dropdown статуса предлагает только валидные переходы: нарисованная кнопка
 * обещает действие, а тут она обещает 400 либо «успех» без изменения.
 *
 * ПОЧЕМУ ИНТЕРИМ, А НЕ ПОЛНОЕ РЕШЕНИЕ. Исходный статус участника в контракте
 * очереди НЕ передаётся: `status` элемента — модерационный
 * (`pending|approved|…`), и сервер схлопывает в него девять статусов участника
 * как `participant.status === CONFIRMED ? approved : pending`
 * (BE `event-admin.service.ts:1023`). Обратное восстановление поэтому
 * определено ровно на одном значении: `approved` ⇒ `CONFIRMED`. За `pending`
 * стоят восемь разных статусов с разными исходами — и среди них есть те, где
 * действие исполняется (`REGISTERED`+approve, `INVITED`+reject), поэтому
 * скрывать там нельзя, а различить их отсюда нечем. Остаток записан в `AD-288`;
 * полное решение — исходный статус в контракте очереди (`AD-260`, партия
 * «Заявки»).
 */
const MODERATION_ACTION_TARGET_STATUS: Record<
  ModerationRowAction,
  ParticipantStatus
> = {
  approve: ParticipantStatus.CONFIRMED,
  reject: ParticipantStatus.CANCELED,
};

/** Обратное восстановление статуса участника из модерационного статуса строки.
 *  Определено только там, где схлопывание на BE обратимо; `Record<string, …>` с
 *  явным `| undefined` — ключ приходит литералом генерата, а не членом enum. */
const MODERATION_STATUS_PARTICIPANT_PREIMAGE: Record<
  string,
  ParticipantStatus | undefined
> = {
  approved: ParticipantStatus.CONFIRMED,
};

export const canExecuteModerationAction = (
  moderationStatus: string,
  action: ModerationRowAction,
): boolean => {
  const current = MODERATION_STATUS_PARTICIPANT_PREIMAGE[moderationStatus];
  // Прообраз неоднозначен — кнопку оставляем: за `pending` стоят и статусы, на
  // которых действие исполняется. Скрыть её здесь значило бы убрать с экрана
  // работающее действие, а это хуже ложной кнопки.
  if (current === undefined) {
    return true;
  }
  const target = MODERATION_ACTION_TARGET_STATUS[action];
  // Цель совпадает с текущим статусом: BE-карта разрешает переход «в себя»
  // (`isParticipantStatusTransitionAllowed`: `next === current` → true), поэтому
  // сервер ответит успехом, но статус не изменится — кнопка обещает изменение,
  // которого не будет. Проверка стоит явно, а не выводится из зеркала ниже:
  // зеркало перехода «в себя» не содержит, и молча опираться на это расхождение
  // с SSOT — значит завязать видимость кнопки на расхождение, а не на правило.
  if (target === current) {
    return false;
  }
  return canTransitionParticipantStatus(current, target);
};

// Реэкспорт DEFAULT_CURRENCY для удобства в компонентах событий
export { DEFAULT_CURRENCY };

/**
 * AD-389 шаг 6. МОЖНО ЛИ ЕЩЁ МЕНЯТЬ ЗАЯВЛЕННЫЙ СПОСОБ УЧАСТИЯ при таком
 * статусе. Зеркало BE `PARTICIPATION_MODE_CHANGEABLE_BY_STATUS`.
 *
 * Запрет точечный и по одной причине: участие уже ЗАКОНЧИЛОСЬ. `ATTENDED` —
 * факт состоялся, `CANCELED`/`REJECTED`/`NO_SHOW` — не состоится; менять
 * заявку о будущем задним числом нечему. Всем живым статусам менять можно:
 * намерение по природе меняется до самого события.
 *
 * ЗАЧЕМ ЗЕРКАЛО НА ЭКРАНЕ, если решает всё равно сервер. Чтобы не показывать
 * кнопку, ведущую в отказ: нажатие при `attended` вернуло бы
 * `PARTICIPATION_MODE_NOT_CHANGEABLE`, и человек прочитал бы красную плашку
 * вместо того, чтобы не увидеть бессмысленного действия. Проверка здесь НЕ
 * заменяет серверную — между отрисовкой и нажатием организатор успевает
 * отметить явку, и на этот случай код отказа разбирается отдельно.
 */
export const PARTICIPATION_MODE_CHANGEABLE_BY_STATUS: Record<
  ParticipantStatus,
  boolean
> = {
  [ParticipantStatus.REGISTERED]: true,
  [ParticipantStatus.CONFIRMED]: true,
  [ParticipantStatus.WAITLISTED]: true,
  [ParticipantStatus.INVITED]: true,
  [ParticipantStatus.PENDING]: true,
  [ParticipantStatus.ATTENDED]: false,
  [ParticipantStatus.CANCELED]: false,
  [ParticipantStatus.REJECTED]: false,
  [ParticipantStatus.NO_SHOW]: false,
};

/**
 * Можно ли ещё менять способ участия. Статус приходит из ответа сервера, и
 * значение вне словаря (данные старше словаря) считается «нельзя»: умолчание —
 * запрет, иначе незнакомый статус молча получил бы право на действие.
 */
export const canChangeParticipationMode = (
  status: ParticipantStatus | undefined,
): boolean =>
  status !== undefined &&
  PARTICIPATION_MODE_CHANGEABLE_BY_STATUS[status] === true;

/**
 * AD-416. Пустит ли сервер участника в чат живого экрана. Зеркало BE
 * `EVENT_CHAT_ADMITTED_STATUSES` (`event-chat-access.ts`): `registered`,
 * `confirmed`, `attended`. Карточка по нему решает, показывать ли гостю вход на
 * `/events/:id/live`, — дверь, за которой гарантированный отказ, не рисуется.
 * Полная карта, а не список: новое значение перечисления без решения здесь не
 * скомпилируется. Незнакомое значение из ответа — «нельзя», как и у сервера.
 * Это только показ; пускает сервер.
 */
export const LIVE_EVENT_ADMITTED_BY_STATUS: Record<ParticipantStatus, boolean> =
  {
    [ParticipantStatus.REGISTERED]: true,
    [ParticipantStatus.CONFIRMED]: true,
    [ParticipantStatus.ATTENDED]: true,
    [ParticipantStatus.WAITLISTED]: false,
    [ParticipantStatus.PENDING]: false,
    [ParticipantStatus.INVITED]: false,
    [ParticipantStatus.CANCELED]: false,
    [ParticipantStatus.REJECTED]: false,
    [ParticipantStatus.NO_SHOW]: false,
  };

export const canEnterLiveEvent = (
  status: ParticipantStatus | undefined,
): boolean =>
  status !== undefined && LIVE_EVENT_ADMITTED_BY_STATUS[status] === true;

/**
 * M2.5. У каких событий места уже продаются: гости записываются, и доплата
 * открывает новые места сразу. Зеркало BE `TRIAL_CONSUMING_BY_STATUS`
 * (`events-core.service.ts`): «боевые» там `published` и `ongoing`. Черновику
 * места открывает публикация (В7), у отменённого и завершённого места не
 * покупаются (409 `EVENT_CAPACITY_EVENT_CLOSED`), а `scheduled` и `postponed`
 * сервер не выставляет вовсе. Полная карта, а не список: новое значение
 * перечисления без решения здесь не скомпилируется; незнакомое значение из
 * ответа — «не продаются».
 */
export const SEATS_ON_SALE_BY_STATUS: Record<EventStatus, boolean> = {
  [EventStatus.DRAFT]: false,
  [EventStatus.SCHEDULED]: false,
  [EventStatus.PUBLISHED]: true,
  [EventStatus.ONGOING]: true,
  [EventStatus.COMPLETED]: false,
  [EventStatus.CANCELLED]: false,
  [EventStatus.POSTPONED]: false,
};

export const areSeatsOnSale = (status: EventStatus | undefined): boolean =>
  status !== undefined && SEATS_ON_SALE_BY_STATUS[status] === true;

/**
 * AD-576 (решение Сергея №49). Кого пропускает отметка прихода — зеркало BE
 * `CHECK_IN_ADMISSION_BY_STATUS` (`checkin-admission.constants.ts`, AD-476):
 * место держат `registered`, `confirmed`, `attended`; остальным сервер отвечает
 * отказом `CHECK_IN_NOT_ADMITTED` с причиной. Правило одно для сервера и для
 * экрана: сканер рисовал кнопку ручной отметки у любого статуса, кроме
 * «отмечен», и кнопка у заявки без решения или у человека из очереди вела в
 * отказ. Полная карта, а не список: новое значение перечисления без решения
 * здесь не скомпилируется. Это только показ; пускает сервер.
 */
export const CHECK_IN_ADMITTED_BY_STATUS: Record<ParticipantStatus, boolean> = {
  [ParticipantStatus.REGISTERED]: true,
  [ParticipantStatus.CONFIRMED]: true,
  [ParticipantStatus.ATTENDED]: true,
  [ParticipantStatus.PENDING]: false,
  [ParticipantStatus.WAITLISTED]: false,
  [ParticipantStatus.INVITED]: false,
  [ParticipantStatus.CANCELED]: false,
  [ParticipantStatus.REJECTED]: false,
  [ParticipantStatus.NO_SHOW]: false,
};

/**
 * Предлагать ли ручную отметку: сервер пропустит, и приход ещё не отмечен
 * (повторная отметка `attended` ничего не пишет). Незнакомое значение из
 * ответа — «нельзя», как и у сервера.
 */
export const canMarkArrival = (
  status: ParticipantStatus | undefined,
): boolean =>
  status !== undefined &&
  status !== ParticipantStatus.ATTENDED &&
  CHECK_IN_ADMITTED_BY_STATUS[status] === true;
